import * as React from "react";
import { ScrollView, Platform, FlatList } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedScrollHandler,
  interpolate,
  interpolateColor,
  withSpring,
} from "react-native-reanimated";

import Flex from "../Flex";
import Progress from "../Progress";
import Gradient from "../Gradient";
import Text from "../Text";

import { withThemeProps, styled, transformColor } from "../../style";
import { isWeb } from "../../util";
import { useUpdateEffect } from "../../hooks";

const AnimatedList = Animated.createAnimatedComponent(ScrollView);
const AnimatedFlatList = Animated.createAnimatedComponent(FlatList);
const AnimatedFlex = Animated.createAnimatedComponent(Flex);

export const getProgress = (a: number, b: number, v: number) => {
  "worklet";
  const p = (v - a) / (b - a);
  return parseFloat(p.toFixed(3));
};

const MAX = 999999999;

function Navbar(
  {
    theme,
    title,
    subTitle,
    titleLeft = null,
    titleProps = {},
    subTitleProps = {},
    top,
    offset = 20,
    distance = 100,
    roundness = 0,
    leftAction = null,
    rightAction = null,
    maxWidth = "99%",
    gap = 10,
    bg = "primary",
    color = "text",
    stopColor = "#FFF",
    stopScale = 0.8,
    stopOpacity = 1,
    gradient = ["accent", "primary"],
    animateBg = true,
    collapsible = false,
    inset = 0,
    animateTitleColor,
    footerComponent = null,
    ...rest
  },
  ref
) {
  const [dynamicTitle, setTitle] = React.useState(title);

  React.useImperativeHandle(ref, () => ({
    setTitle,
  }));

  useUpdateEffect(() => {
    setTitle(title);
  }, [title]);

  const animatedStyle = useAnimatedStyle(() => {
    const y = interpolate(top.value, [0, distance, MAX], [offset, 0, 0]);
    return {
      transform: [
        {
          translateY: withSpring(y),
        },
      ],
    };
  }, []);

  const animatedBg = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        top.value,
        [0, distance, MAX],
        [0, stopOpacity, stopOpacity]
      ),
    };
  }, []);

  const animatedTitle = useAnimatedStyle(() => {
    const scale = interpolate(
      top.value,
      [0, distance, MAX],
      [1, stopScale, stopScale]
    );
    return {
      transform: [
        {
          scale: withSpring(scale),
        },
      ],
    };
  }, []);

  const c1 = transformColor({ value: color, theme, themeKey: "colors" });
  const c2 = transformColor({ value: stopColor, theme, themeKey: "colors" });
  const animatedColorStyle = useAnimatedStyle(() => {
    return {
      color: animateTitleColor
        ? interpolateColor(top.value, [0, distance], [c1, c2])
        : c1,
    };
  }, [c1, c2]);

  return (
    <AnimatedFlex
      pt={inset}
      w="100%"
      zIndex={999}
      center
      style={animatedStyle}
      {...rest}
    >
      <AnimatedFlex
        w="100%"
        h={200}
        absolute
        r={0}
        l={0}
        b={0}
        bg={bg}
        borderRadius={roundness}
        style={animateBg ? animatedBg : {}}
        overflow="hidden"
      >
        {gradient ? <Gradient colors={gradient} absoluteFill /> : null}
      </AnimatedFlex>
      <Flex
        w="100%"
        h={55}
        justifyContent="space-between"
        alignItems="center"
        maxWidth={maxWidth}
        px={gap}
        row
      >
        <Flex zIndex={888}>{leftAction}</Flex>
        <AnimatedFlex
          zIndex={555}
          center
          style={animatedTitle}
          absoluteFill
          pointerEvents="box-none"
        >
          <Flex w="100%" center row>
            {titleLeft}
            <Text
              maxWidth="60%"
              font="h5"
              key={title}
              numberOfLines={1}
              {...titleProps}
            >
              {dynamicTitle}
            </Text>
          </Flex>
          {subTitle ? (
            <Text
              maxWidth="50%"
              opacity={0.5}
              numberOfLines={1}
              font="label"
              {...subTitleProps}
            >
              {subTitle}
            </Text>
          ) : null}
        </AnimatedFlex>
        <Flex zIndex={888}>{rightAction}</Flex>
      </Flex>
      {footerComponent}
    </AnimatedFlex>
  );
}

const NavBarWithRef = React.forwardRef(Navbar);

interface Props {
  children: React.ReactNode;
  onPress: void;
  theme: object;
  rounded?: boolean;
  light?: boolean;
  outlined?: boolean;
  [key: string]: any;
}

const Page = (
  {
    theme,
    bg = "background",
    height = "100vh",
    children,
    scrollable = true,
    renderHeader,
    renderFooter,
    scrollProps,
    scrollComponent,
    scrollWrapperComponent,
    scrollWrapperProps = {},
    onScroll,
    scrollTop,
    disableSafeArea = false,
    scrollAnimated = true,
    useFlatList = false,
    withNavbar = true,
    navbarProps = {},
    navbarComponent,
    title,
    subTitle,
    titleLeft,
    leftAction = null,
    rightAction = null,
    loading = false,
    loadingText = null,
    ...rest
  }: Props,
  ref
) => {
  const insets = useSafeAreaInsets();
  const inset = disableSafeArea ? 0 : insets.top;
  const offset = navbarProps.offset || 20;
  const spacerHeight = offset;
  const scrollViewRef = React.useRef(null);
  const top = useSharedValue(0);

  const scrollHandler = useAnimatedScrollHandler(
    {
      onScroll: (event) => {
        const { y } = event.contentOffset;
        top.value = y;
      },
    },
    []
  );

  const animatedStyle = useAnimatedStyle(() => {
    const y = interpolate(top.value, [0, 100, MAX], [offset, 0, 0]);
    return {
      transform: [
        {
          translateY: withSpring(y),
        },
      ],
    };
  }, []);

  const getRef = React.useCallback(() => {
    console.log({ scrollViewRef });
    if (!scrollViewRef.current) return;
    return scrollViewRef.current;
  }, [scrollViewRef]);

  const scrollTo = React.useCallback(
    (offset: number, animated = true) => {
      if (getRef()) {
        if (useFlatList) {
          getRef().scrollToOffset({ offset, animated });
        } else {
          getRef().scrollTo({ x: offset, y: 0, animated });
        }
      } else if (isWeb) {
        document?.getElementById("scroller")?.scrollTo(0, 0);
      }
    },
    [getRef]
  );

  const ScrollWrapperComp = scrollWrapperComponent || React.Fragment;
  const ScrollComp = useFlatList ? AnimatedFlatList : AnimatedList;
  const Scroller = scrollable ? scrollComponent || ScrollComp : React.Fragment;
  const ScrollerProps = React.useMemo(() => {
    return {
      ref: scrollViewRef,
      scrollTo,
      keyboardShouldPersistTaps: "handled",
      onScroll: scrollHandler,
      scrollEventThrottle: 16,
      showsVerticalScrollIndicator: false,
      ...scrollProps,
      style: {
        ...(scrollProps?.style || {}),
        flex: 1,
        paddingTop: useFlatList ? (isWeb ? spacerHeight / 2 : 0) : 0,
        paddingBottom: spacerHeight,
        ...animatedStyle,
      },
    };
  }, [useFlatList, scrollViewRef, scrollProps, spacerHeight]);

  return (
    <Flex
      h={height}
      bg={bg}
      accessibilityRole={Platform.OS === "web" ? "main" : "none"}
      webStyle={{ transitionDuration: "0.5s", transitionProperty: "all" }}
      {...rest}
    >
      {withNavbar ? (
        <NavBarWithRef
          ref={ref}
          theme={theme}
          title={loading ? "..." : title}
          titleLeft={titleLeft}
          subTitle={subTitle}
          leftAction={leftAction}
          rightAction={rightAction}
          top={top}
          inset={inset}
          {...navbarProps}
        />
      ) : null}
      {renderHeader ? renderHeader({ top }) : null}
      {loading ? (
        <Flex w="100%" flex={1} py={50} center>
          <Progress size={30} trackWidth={3} progressWidth={3} loading />
          {loadingText ? (
            <Text font="label" mt={10}>
              {loadingText}
            </Text>
          ) : null}
        </Flex>
      ) : (
        <ScrollWrapperComp {...scrollWrapperProps}>
          <Scroller nativeID="scroller" {...(scrollable ? ScrollerProps : {})}>
            {useFlatList || !withNavbar || !scrollable ? null : (
              <Flex h={spacerHeight} w="100%" />
            )}
            {children}
          </Scroller>
        </ScrollWrapperComp>
      )}
      {renderFooter ? renderFooter({ top }) : null}
    </Flex>
  );
};

export default withThemeProps(React.forwardRef(Page), "Page");
